import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { GALLERY_CANNOT_PURCHASE_ERROR_TYPE } from '@/constants/purchaseErrors';
import usePaymentProfile from "@/hooks/usePaymentProfile";
import useTranslations from "@/i18n/useTranslations";
import paymentProfileService from "@/services/paymentProfile.service";
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import ShippingDetailsPurchaseLoader from "@/shared-components/loader/special-loaders/ShippingDetailsPurchaseLoader";

import "./PaymentMethodPanel.css";

function normalizeMethod(candidate, defaultPaymentMethodId = null) {
  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const card =
    candidate.card ||
    candidate.payment_method_details?.card ||
    candidate.payment_method?.card ||
    candidate.details?.card ||
    candidate.billing_details?.card ||
    {};

  const id =
    candidate.id ||
    candidate.payment_method_id ||
    candidate.payment_method ||
    candidate.stripe_payment_method_id ||
    candidate._id ||
    null;

  if (!id) {
    return null;
  }

  const brand = card.brand || candidate.brand || candidate.card_brand || "Card";
  const last4 = String(card.last4 ?? candidate.last4 ?? candidate.card_last4 ?? "").slice(-4);
  const expMonth = card.exp_month ?? candidate.exp_month ?? null;
  const expYear = card.exp_year ?? candidate.exp_year ?? null;
  const billingName =
    candidate.billing_name ||
    candidate.billing_details?.name ||
    candidate.cardholder_name ||
    null;
  const country = (card.country || candidate.country || candidate.card_country || "").toUpperCase();

  return {
    id,
    brand,
    last4,
    expMonth,
    expYear,
    billingName,
    country: country || null,
    isDefault:
      Boolean(candidate.is_default ?? candidate.isDefault ?? candidate.default) ||
      (defaultPaymentMethodId ? id === defaultPaymentMethodId : false),
  };
}

export default function PurchasePaymentMethodPanel({ onManagePaymentMethods }) {
  const t = useTranslations('common.shared.artwork_view.payment_methods');
  const tPurchaseRejection = useTranslations('common.shared.artwork_view.purchase_rejection');
  const paymentProfile = usePaymentProfile();
  const ensurePaymentProfile = paymentProfile?.ensurePaymentProfile;
  const isGalleryAccount = paymentProfile?.status === 'unsupported';
  const bidderCountryHint =
    paymentProfile?.profile?.country ??
    paymentProfile?.profile?.country_iso ??
    paymentProfile?.profile?.country_code ??
    null;
  const navigate = useNavigate();

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addingMethod, setAddingMethod] = useState(false);

  const getPaymentErrorMessage = useCallback((cause, fallbackKey) => {
    const message = String(cause?.message ?? '').toLowerCase();
    // Keep the text fallback for older payment-profile responses without a code.
    const isUnsupportedGalleryError = cause?.code === 'BIDDER_PROFILE_UNSUPPORTED'
      || cause?.code === GALLERY_CANNOT_PURCHASE_ERROR_TYPE
      || message.includes('gallery accounts cannot');

    if (isUnsupportedGalleryError) {
      return tPurchaseRejection('gallery_account');
    }

    return cause?.message ?? t(fallbackKey);
  }, [t, tPurchaseRejection]);

  const loadPaymentMethods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { response, data } = await paymentProfileService.listPaymentMethods();
      if (!response?.ok) {
        throw new Error(data?.error_message ?? t('errors.unable_to_load'));
      }
      const list =
        (Array.isArray(data?.payment_methods) && data.payment_methods) ||
        (Array.isArray(data?.results) && data.results) ||
        (Array.isArray(data?.items) && data.items) ||
        [];
      const defaultMethodId = data?.default_payment_method_id ?? data?.default_payment_method ?? null;
      const normalized = list
        .map((method) => normalizeMethod(method, defaultMethodId))
        .filter(Boolean);
      setPaymentMethods(normalized);
    }
    catch (loadError) {
      setError(getPaymentErrorMessage(loadError, 'errors.unable_to_load'));
      setPaymentMethods([]);
    }
    finally {
      setLoading(false);
    }
  }, [getPaymentErrorMessage, t]);

  useEffect(() => {
    if (!isGalleryAccount) {
      loadPaymentMethods();
    }
  }, [isGalleryAccount, loadPaymentMethods]);

  const handleAddPaymentMethod = useCallback(async () => {
    if (typeof ensurePaymentProfile !== "function") {
      navigate("/profile/settings/payment-methods");
      return;
    }
    setAddingMethod(true);
    try {
      await ensurePaymentProfile({
        forceSetup: true,
        countryHint: bidderCountryHint ?? undefined,
      });
      await loadPaymentMethods();
    }
    catch (modalError) {
      if (modalError?.code === "BIDDER_PROFILE_CANCELLED") {
        return;
      }
      setError(getPaymentErrorMessage(modalError, 'errors.unable_to_add'));
    }
    finally {
      setAddingMethod(false);
    }
  }, [bidderCountryHint, ensurePaymentProfile, getPaymentErrorMessage, loadPaymentMethods, navigate]);

  if (isGalleryAccount) {
    return null;
  }

  return (
    <section className="purchase-payment-methods">
      <div className="purchase-payment-methods__header">
        <h3 className="purchase-payment-methods__title">{t('title')}</h3>
        <button
          type="button"
          className="purchase-payment-methods__manage"
          onClick={onManagePaymentMethods ?? (() => navigate("/profile/settings/payment-methods"))}
        >
          {t('manage')}
        </button>
      </div>

      {loading ? (
        <div className="purchase-payment-methods__spinner">
          <ShippingDetailsPurchaseLoader className="purchase-payment-methods__spinner-loader" />
        </div>
      ) : paymentMethods.length > 0 ? (
        <>
          <ul className="purchase-payment-methods__list">
            {paymentMethods.map((method) => (
              <li
                key={method.id}
                className={`purchase-payment-methods__item${method.isDefault ? " default" : ""}`}
              >
                <div className="purchase-payment-methods__item-row">
                  <span className="purchase-payment-methods__brand">{method.brand}</span>
                  <span className="purchase-payment-methods__last4">•••• {method.last4}</span>
                </div>
                <div className="purchase-payment-methods__item-row">
                  <span className="purchase-payment-methods__meta">
                    {t('expires')}{" "}
                    {method.expMonth && method.expYear
                      ? `${String(method.expMonth).padStart(2, "0")}/${String(method.expYear).slice(-2)}`
                      : "—"}
                  </span>
                  {method.isDefault && (
                    <span className="purchase-payment-methods__badge">{t('default_badge')}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <ContinueButton
            type="button"
            onClick={handleAddPaymentMethod}
            loading={addingMethod}
          >
            {t('add')}
          </ContinueButton>
        </>
      ) : (
        <div className="purchase-payment-methods__empty">
          <ContinueButton
            type="button"
            onClick={handleAddPaymentMethod}
            loading={addingMethod}
          >
            {t('add')}
          </ContinueButton>
        </div>
      )}

      {error && (
        <p className="purchase-payment-methods__feedback" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
